import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { AuthHeader } from "@/components/auth/AuthHeader";
import { AuthErrorAlert } from "@/components/auth/AuthErrorAlert";
import { LoginForm } from "@/components/auth/LoginForm";
import { SignupForm } from "@/components/auth/SignupForm";
import { useToast } from "@/hooks/use-toast";

const AuthPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [authStatus, setAuthStatus] = useState<string | null>(null);
  const [showPasswordUpdate, setShowPasswordUpdate] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  useEffect(() => {
    // Reset guidelines acceptance on login page visit
    localStorage.removeItem('guidelines-accepted');

    // --- Begin Supabase fragment parsing ---
    const hash = window.location.hash.substring(1); // Remove leading '#'
    const params = new URLSearchParams(hash);

    const access_token = params.get('access_token');
    const refresh_token = params.get('refresh_token');
    const type = params.get('type'); // Should be 'recovery' for password reset

    if (access_token && refresh_token) {
      console.log("Found tokens in URL fragment, setting session...");
      // Set the Supabase session so getSession() works!
      supabase.auth.setSession({
        access_token,
        refresh_token
      }).then(() => {
        console.log("Session set successfully");
        // Clean up the URL for security & cleanliness
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);

        // Show password update UI if this is a recovery flow
        if (type === 'recovery' || searchParams.get('reset') === 'true') {
          console.log("Password recovery flow detected from fragment");
          setShowPasswordUpdate(true);
        }
      });
      return;
    }
    // --- End Supabase fragment parsing ---

    const checkAuthStatus = async () => {
      const { data } = await supabase.auth.getSession();
      
      // Check if this is a password reset redirect
      const isPasswordReset = searchParams.get('reset') === 'true';
      if (isPasswordReset) {
        console.log("Password recovery flow detected from search params");
        setShowPasswordUpdate(true);
        return;
      }
      
      // If user is already logged in and not in recovery flow, redirect to main page
      if (data.session) {
        console.log("User already logged in, redirecting to main page");
        navigate("/");
      }
    };

    checkAuthStatus();
  }, [navigate, searchParams]);

  const handleLoginSuccess = () => {
    navigate("/");
  };

  const handleSignupSuccess = () => {
    navigate("/");
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setAuthStatus("Passordene samsvarer ikke");
      return;
    }

    if (newPassword.length < 6) {
      setAuthStatus("Passordet må være minst 6 tegn langt");
      return;
    }

    setIsLoading(true);
    setAuthStatus(null);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        setAuthStatus(error.message);
      } else {
        toast({
          title: "Passord oppdatert!",
          description: "Passordet ditt har blitt oppdatert. Du blir nå logget inn.",
        });
        setShowPasswordUpdate(false);
        navigate("/");
      }
    } catch (error) {
      setAuthStatus("Det oppstod en feil ved oppdatering av passord.");
    } finally {
      setIsLoading(false);
    }
  };

  if (showPasswordUpdate) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <div className="p-4">
          <Button
            variant="ghost"
            onClick={() => {
              setShowPasswordUpdate(false);
              navigate('/auth');
            }}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            Tilbake
          </Button>
        </div>
        
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="w-full max-w-md">
            <Card>
              <CardHeader>
                <CardTitle>Oppdater passord</CardTitle>
                <CardDescription>
                  Oppgi ditt nye passord
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AuthErrorAlert message={authStatus} />
                <form onSubmit={handlePasswordUpdate} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">Nytt passord</Label>
                    <Input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Bekreft nytt passord</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? "Oppdaterer..." : "Oppdater passord"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <div className="p-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          Tilbake
        </Button>
      </div>
      
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-8 mb-8">
            <div className="flex-1 mt-4">
              <AuthHeader 
                title="Innlogging Klara" 
                description="Logg inn til din konto" 
              />
            </div>
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg">
              <img
                src="/lovable-uploads/ec7866fd-a04d-4cd4-b93b-6802d84f0f3f.png"
                alt="Klara illustrasjonsbilde"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <AuthErrorAlert message={authStatus} />
          
          <Tabs defaultValue="login" className="w-full">
            
            <TabsContent value="login">
              <Card>
                <CardHeader>
                  <CardTitle>Logg inn</CardTitle>
                  <CardDescription>
                    Logg inn med din konto
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <LoginForm 
                    setIsLoading={setIsLoading} 
                    setAuthStatus={setAuthStatus}
                    onLoginSuccess={handleLoginSuccess}
                  />
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
